import {Button, Card, CTABanner, FAQ, Grid, Link, Section, SectionIntro, Stack, Text} from '@primer/react-brand'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import {renderWithNoMarks} from '../../../utils'
import type {OutroSectionProps} from './types'

export const OutroSection = ({
  outroCustomerStories,
  outroCtaBanner,
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  outroIncludeResources,
  outroResourcesHeading,
  outroResources,
  outroFaqs,
  outroFaqsHeading,
}: OutroSectionProps) => {
  const groupsWithFaqs = outroFaqs?.filter(group =>
    group.fields.faqs.some(faq => faq.fields.question && faq.fields.answer),
  )

  const hasCustomerStories = outroCustomerStories && outroCustomerStories.length > 0

  return (
    <Section>
      {hasCustomerStories &&
        outroCustomerStories.map(story => {
          const {logo, lead, url, title} = story.fields
          return (
            <Stack key={story.sys.id} direction="horizontal" alignItems="center" gap="normal">
              <img src={logo.fields.file.url} alt={logo.fields.description || title} />
              <Stack>
                <Text variant="muted">{lead}</Text>
                <Link href={url} variant="accent">
                  {title}
                </Link>
              </Stack>
            </Stack>
          )
        })}

      {outroCtaBanner && (
        <CTABanner>
          {outroCtaHeading && <CTABanner.Heading>{renderWithNoMarks(outroCtaHeading)}</CTABanner.Heading>}
          {outroCtaDescription && (
            <CTABanner.Description>{renderWithNoMarks(outroCtaDescription)}</CTABanner.Description>
          )}
          {outroCtaOne?.fields.href && outroCtaTwo?.fields.href ? (
            <CTABanner.ButtonGroup>
              <Button as="a" href={outroCtaOne.fields.href}>
                {outroCtaOne.fields.text}
              </Button>
              <Button as="a" href={outroCtaTwo.fields.href} variant="secondary">
                {outroCtaTwo.fields.text}
              </Button>
            </CTABanner.ButtonGroup>
          ) : outroCtaOne?.fields.href ? (
            <CTABanner.ButtonGroup>
              <Button as="a" href={outroCtaOne.fields.href}>
                {outroCtaOne.fields.text}
              </Button>
            </CTABanner.ButtonGroup>
          ) : null}
        </CTABanner>
      )}

      {outroIncludeResources && (
        <>
          {outroResourcesHeading && (
            <SectionIntro align="center">
              <SectionIntro.Heading>{renderWithNoMarks(outroResourcesHeading)}</SectionIntro.Heading>
            </SectionIntro>
          )}
          <Grid>
            {outroResources?.map(resource => {
              const Octicon = getPrimerIcon(resource.fields.icon)
              return (
                <Grid.Column key={resource.sys.id}>
                  <Card href={resource.fields.href}>
                    {Octicon && <Card.Icon icon={<Octicon />} color="default" hasBackground />}
                    <Card.Heading>{renderWithNoMarks(resource.fields.heading)}</Card.Heading>
                    <Card.Description>{renderWithNoMarks(resource.fields.description)}</Card.Description>
                  </Card>
                </Grid.Column>
              )
            })}
          </Grid>
        </>
      )}

      {outroFaqsHeading && <FAQ.Heading>{renderWithNoMarks(outroFaqsHeading)}</FAQ.Heading>}
      {groupsWithFaqs?.map(group => {
        const completeFaqs = group.fields.faqs.filter(faq => faq.fields.question && faq.fields.answer)

        return (
          <FAQ key={group.sys.id}>
            {group.fields.heading && <FAQ.Heading>{group.fields.heading}</FAQ.Heading>}
            {completeFaqs.map(faq => (
              <FAQ.Item key={faq.sys.id}>
                <FAQ.Question>{faq.fields.question}</FAQ.Question>
                <FAQ.Answer>{renderWithNoMarks(faq.fields.answer)}</FAQ.Answer>
              </FAQ.Item>
            ))}
          </FAQ>
        )
      })}
    </Section>
  )
}
